import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { usePost } from '../../sharedModules/hooks/useQuery';
import { buildEmailPreviewHtml, EMAIL_WIDTH } from './buildEmailPreviewHtml';

// The email table plus the shell's 16px grey gutter on each side (buildEmailPreviewHtml).
const NATURAL_WIDTH = EMAIL_WIDTH + 2 * 16;

// Waits for a pause in typing before asking the server to render, so a burst of keystrokes is one request.
const RENDER_DEBOUNCE_MS = 300;

interface RenderedPreview {
  rendered_subject: string;
  rendered_body: string;
}

interface Props {
  // Backend endpoint that renders the unsaved template against sample data with the real renderer and
  // invoice-table partial (Collections::PreviewSample) — so the preview cannot drift from sent emails.
  previewUrl: string;
  subject: string;
  body: string;
  senderDisplayName: string;
  replyToEmail: string;
  // Extra unsaved values the renderer needs (e.g. thresholds on the collections page).
  extraParams?: Record<string, number>;
}

const EmailTemplatePreview: React.FC<Props> = ({
  previewUrl,
  subject,
  body,
  senderDisplayName,
  replyToEmail,
  extraParams,
}) => {
  const { mutateAsync } = usePost<RenderedPreview>();
  const [rendered, setRendered] = useState<RenderedPreview | null>(null);
  const [failed, setFailed] = useState(false);
  const latestRequest = useRef(0);
  const extraParamsKey = JSON.stringify(extraParams ?? {});

  useEffect(() => {
    const timer = setTimeout(async () => {
      latestRequest.current += 1;
      const requestId = latestRequest.current;
      try {
        const response = await mutateAsync({
          url: previewUrl,
          data: { subject, body, ...JSON.parse(extraParamsKey) },
        });
        // Responses can arrive out of order; only the newest request may update the preview.
        if (requestId !== latestRequest.current) return;
        setRendered(response.data);
        setFailed(false);
      } catch {
        // Keep showing the last good render; a transient failure shouldn't blank the preview.
        if (requestId === latestRequest.current) setFailed(true);
      }
    }, RENDER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [previewUrl, subject, body, extraParamsKey, mutateAsync]);

  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [containerWidth, setContainerWidth] = useState(NATURAL_WIDTH);
  const [contentSize, setContentSize] = useState({ width: NATURAL_WIDTH, height: 600 });

  // The email is at least 600px wide, wider than the half-page column: render it at its real size and
  // scale it down to fit (never up) instead of showing scrollbars.
  const scale = Math.min(1, containerWidth / contentSize.width);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const observer = new ResizeObserver(([entry]) => setContainerWidth(entry.contentRect.width));
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Measure the content after every reload. The width matters too: content can overflow the 600px card
  // (the statement table is nowrap, long URLs don't wrap) and must stay visible rather than be clipped.
  // Reset to the natural width first — scrollWidth never reports less than the iframe's current width,
  // so the preview could otherwise never shrink back after a wide edit.
  const measureContent = () => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (!iframe || !doc) return;
    iframe.style.width = `${NATURAL_WIDTH}px`;
    const { scrollWidth, scrollHeight } = doc.documentElement;
    const width = Math.max(NATURAL_WIDTH, scrollWidth);
    // Restore directly: React skips re-applying the style when the measured width hasn't changed.
    iframe.style.width = `${width}px`;
    setContentSize({ width, height: scrollHeight });
  };

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Live Preview — sample data, not a real customer
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
        <strong>Subject:</strong> {rendered?.rendered_subject ?? ''}
      </Typography>
      {failed && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          Preview could not be refreshed — showing the last successful render.
        </Typography>
      )}
      {/* Container stays mounted so the ResizeObserver attached on first render keeps working. */}
      <Box ref={containerRef} sx={{ width: '100%' }}>
        {/* The iframe mounts only once the first render arrives: mounting with an empty srcDoc and filling
            it later can leave it blank (see ScheduledEmailModal). */}
        {!rendered && (
          <Typography variant="body2" color="textSecondary">
            Loading preview...
          </Typography>
        )}
        {rendered && (
          <Box
            sx={{
              width: contentSize.width * scale,
              height: contentSize.height * scale,
              mx: 'auto',
              overflow: 'hidden',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
            }}
          >
            <iframe
              ref={iframeRef}
              srcDoc={buildEmailPreviewHtml(rendered.rendered_body, senderDisplayName, replyToEmail)}
              // allow-same-origin lets measureContent read the height; scripts stay blocked (no allow-scripts).
              sandbox="allow-same-origin"
              scrolling="no"
              onLoad={measureContent}
              title="Email template preview"
              style={{
                width: contentSize.width,
                height: contentSize.height,
                border: 0,
                display: 'block',
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EmailTemplatePreview;
